<template>
  <div class="biz-announcements-page">
    <div class="page-header">
      <h1>Ogłoszenia firmowe</h1>
      <button class="btn-primary" @click="$router.push('/dla-biznesu/profile/announcements/add')">
        + Dodaj ogłoszenie
      </button>
    </div>

    <section class="section-card position-relative" v-if="isEditModalVisible">
      <div class="section-card-header">
        <h2>Edycja ogłoszenia</h2>
        <p class="section-subtitle">
          Zmień dane ogłoszenia. Po zapisaniu ogłoszenie ponownie trafi do weryfikacji.
        </p>
      </div>

      <form class="edit-form" @submit.prevent="editAnnouncement">
        <div class="form-group">
          <label for="editTitle">Tytuł</label>
          <input
            id="editTitle"
            type="text"
            placeholder="Tytuł"
            v-model="editModalDetails.titleValue"
            name="title"
            required
            maxlength="50"
            minlength="3"
          >
        </div>

        <div class="form-group">
          <label for="editShortDescription">Krótki opis</label>
          <textarea
            id="editShortDescription"
            placeholder="Krótki opis"
            v-model="editModalDetails.shortDescriptionValue"
            name="short_description"
            required
            maxlength="350"
            minlength="20"
          ></textarea>
        </div>

        <div class="form-group">
          <label for="editContent">Treść</label>
          <textarea
            id="editContent"
            placeholder="Treść ogłoszenia"
            v-model="editModalDetails.contentValue"
            name="content"
            required
            maxlength="3000"
            minlength="80"
          ></textarea>
        </div>

        <div class="form-group">
          <label for="editContact">Adres e-mail do kontaktu</label>
          <input
            id="editContact"
            type="email"
            placeholder="Adres e-mail do kontaktu"
            autocomplete="email"
            v-model="editModalDetails.contact"
            maxlength="50"
            required
          >
        </div>

        <div class="form-row">
          <div class="form-col">
            <label for="editTarget">Odbiorcy</label>
            <select name="target" id="editTarget" v-model="editModalDetails.target" required>
              <option>do wszystkich</option>
              <option>do specjalistów</option>
              <option>do właścicieli zwierząt</option>
            </select>
          </div>

          <div class="form-col">
            <label for="editCategory">Kategoria</label>
            <select name="category" id="editCategory" v-model="editModalDetails.category" required>
              <option>sprzedaż</option>
              <option>współpraca</option>
              <option>wymiana</option>
              <option>inne</option>
            </select>
          </div>
        </div>

        <div class="form-group">
          <label for="announcementImage">Zdjęcie wyróżniające</label>
          <input
            type="file"
            id="announcementImage"
            @change="onFileUpload"
            accept="image/png, image/jpeg"
          >
        </div>

        <div class="form-actions">
          <button type="submit" class="btn-green">Zatwierdź</button>
          <button type="button" class="btn-outline" @click="closeEditModal">Anuluj</button>
        </div>
      </form>
    </section>

    <section class="section-card position-relative" v-if="currentUser && announcements.length > 0">
      <HintComponent page-key="business_panel_announcement_list" :absolute="true" />

      <div class="section-card-header">
        <h2>Lista ogłoszeń</h2>
        <p class="section-subtitle">
          Zarządzaj ogłoszeniami dodanymi z konta biznesowego.
        </p>
      </div>

      <div class="announcements-container">
        <AnnouncementCardVue
          :show-active="true"
          v-for="announcement in announcements"
          :key="announcement.id"
          :contact="announcement.contact"
          :active="announcement.active"
          :target="announcement.target"
          :category="announcement.category"
          :img="announcement.photo_url"
          :longContent="announcement.description"
          :id="announcement.id"
          :title="announcement.title"
          :content="announcement.short_description"
          :editable="true"
          :removable="true"
          :reportable="false"
          :photo="announcement.photo"
          @deleteAnnouncement="deleteAnnouncement"
          @showEditModal="showEditModal"
        />

        <button
          v-if="announcements.length >= announcementsLimit"
          class="btn-outline btn-load-more"
          @click="loadMoreAnnouncements"
        >
          Załaduj więcej...
        </button>
      </div>
    </section>

    <section class="section-card position-relative" v-else>
      <HintComponent page-key="business_panel_announcements" :absolute="true" />

      <div class="empty-state">
        <p>Brak ogłoszeń firmowych.</p>
        <button class="btn-primary" @click="$router.push('/dla-biznesu/profile/announcements/add')">
          Dodaj pierwsze ogłoszenie
        </button>
      </div>
    </section>
  </div>
</template>

<script>
import { useMeta } from 'vue-meta';
import axios from 'axios';
import AnnouncementCardVue from '@/components/SpecialComponents/AnnouncementCard.vue';
import HintComponent from '@/components/SpecialComponents/HintComponent.vue';
import authHeader from '@/services/auth-header';
import { useAuthStore } from '@/stores/auth';
import { useAppStore } from '@/stores/app';

export default {
  name: 'BizProfileAnnouncementsVue',
  components: {
    AnnouncementCardVue,
    HintComponent
  },
  setup() {
    useMeta({ title: 'Ogłoszenia firmowe' });

    return {
      authStore: useAuthStore(),
      appStore: useAppStore()
    };
  },
  data() {
    return {
      announcements: [],
      announcementsLimit: 10,
      isEditModalVisible: false,
      editModalDetails: {
        titleValue: '',
        shortDescriptionValue: '',
        contentValue: '',
        id: '',
        contact: '',
        target: '',
        category: '',
        photo: null
      }
    };
  },
  computed: {
    currentUser() {
      return this.authStore.user;
    }
  },
  mounted() {
    if (!this.currentUser) {
      this.$router.push('/login');
    } else if (this.currentUser.role_id !== 2) {
      this.$router.push('/login');
    } else {
      this.getMyAnnouncements(this.announcementsLimit);
    }
  },
  methods: {
    getMyAnnouncements(limit) {
      const ob = {
        user_id: this.currentUser.id,
        page: 1,
        limit: limit
      };

      this.announcements = [];

      axios.post('/AnnouncementSearch', ob).then((res) => {
        res.data.items.forEach((item) => {
          this.announcements.push({
            title: item.title,
            description: item.description,
            id: item.id,
            photo_url: item.photo_url,
            short_description: item.short_description,
            target: item.target,
            category: item.category,
            contact: item.contact,
            active: item.active
          });
        });
      });
    },
    onFileUpload(e) {
      this.editModalDetails.photo = e.target.files[0];
    },
    showEditModal({
                    titleValue,
                    shortDescriptionValue,
                    contentValue,
                    id,
                    target,
                    contact,
                    category
                  }) {
      this.isEditModalVisible = true;
      this.editModalDetails.titleValue = titleValue;
      this.editModalDetails.shortDescriptionValue = shortDescriptionValue;
      this.editModalDetails.contentValue = contentValue;
      this.editModalDetails.id = id;
      this.editModalDetails.target = target;
      this.editModalDetails.contact = contact;
      this.editModalDetails.category = category;
      this.editModalDetails.photo = null;
    },
    closeEditModal() {
      this.isEditModalVisible = false;
      this.editModalDetails = {
        titleValue: '',
        shortDescriptionValue: '',
        contentValue: '',
        id: '',
        contact: '',
        target: '',
        category: '',
        photo: null
      };
    },
    loadMoreAnnouncements() {
      this.announcementsLimit += 5;
      this.getMyAnnouncements(this.announcementsLimit);
    },
    deleteAnnouncement(id) {
      if (confirm('Czy na pewno chcesz usunąć ogłoszenie?')) {
        axios.delete(`/Announcement/${id}`, authHeader()).then((res) => {
          if (String(res.status).charAt(0) === '2') {
            this.appStore.setIsSuccess('Pomyślnie usunięto ogłoszenie');
            this.announcements = this.announcements.filter((item) => item.id !== id);
          }
        });
      }
    },
    editAnnouncement() {
      const ob = {
        id: this.editModalDetails.id,
        title: this.editModalDetails.titleValue,
        short_description: this.editModalDetails.shortDescriptionValue,
        description: this.editModalDetails.contentValue,
        active: 0,
        contact: this.editModalDetails.contact,
        target: this.editModalDetails.target,
        category: this.editModalDetails.category
      };

      if (this.editModalDetails.photo) {
        ob.photo = this.editModalDetails.photo;
      }

      axios.post('/Announcement', ob, {
        headers: {
          ['Content-Type']: 'multipart/form-data'
        }
      }).then((res) => {
        if (String(res.status).charAt(0) === '2') {
          this.appStore.setIsSuccess('Pomyślnie zmieniono ogłoszenie');

          const editedAnnouncement = this.announcements.find((item) => item.id === this.editModalDetails.id);

          if (editedAnnouncement) {
            editedAnnouncement.title = this.editModalDetails.titleValue;
            editedAnnouncement.description = this.editModalDetails.contentValue;
            editedAnnouncement.short_description = this.editModalDetails.shortDescriptionValue;
            editedAnnouncement.contact = this.editModalDetails.contact;
            editedAnnouncement.target = this.editModalDetails.target;
            editedAnnouncement.category = this.editModalDetails.category;
            editedAnnouncement.active = 0;
          }

          this.closeEditModal();
        }
      });
    }
  }
};
</script>

<style scoped lang="scss">
.biz-announcements-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 16px;
  font-family: sans-serif;
  box-sizing: border-box;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e5e7eb;
}

.page-header h1 {
  margin: 0;
  font-size: 2.8rem;
  color: #1f2937;
  font-weight: 700;
}

.section-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, .08);
  padding: 24px;
  margin-bottom: 20px;
  box-sizing: border-box;
}

.section-card-header {
  margin-bottom: 16px;
}

.section-card-header h2 {
  margin: 0;
  font-size: 2rem;
  color: #1f2937;
}

.section-subtitle {
  margin: 6px 0 0 0;
  color: #6b7280;
  font-size: 1.4rem;
}

.announcements-container {
  display: grid;

  grid-template-columns: repeat(3, 1fr);
  gap: 18px;
}

@media (max-width: 1024px) {
  .announcements-container {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 720px) {
  .announcements-container {
    grid-template-columns: 1fr;
  }
}

.edit-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.form-col {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.edit-form label {
  font-size: 1.4rem;
  font-weight: 600;
  color: #374151;
}

.edit-form input,
.edit-form select,
.edit-form textarea {
  width: 100%;
  border: 1px solid #d1d5db;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 1.4rem;
  box-sizing: border-box;
  outline: none;
  background: #fff;
}

.edit-form textarea {
  resize: vertical;
  min-height: 110px;
}

.edit-form input:focus,
.edit-form select:focus,
.edit-form textarea:focus {
  border-color: #10b981;
}

.form-actions {
  display: flex;
  gap: 8px;
  margin-top: 8px;
  padding-top: 12px;
  border-top: 1px solid #e5e7eb;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.btn-primary,
.btn-green,
.btn-outline {
  border-radius: 8px;
  border: none;
  padding: 10px 14px;
  cursor: pointer;
  font-size: 1.4rem;
  font-weight: 600;
}

.btn-primary {
  background: #f97316;
  color: #fff;
}

.btn-primary:hover {
  background: #ea6c0a;
}

.btn-green {
  background: #10b981;
  color: #fff;
}

.btn-green:hover {
  background: #059669;
}

.btn-outline {
  background: #fff;
  color: #1f2937;
  border: 1px solid #d1d5db;
}

.btn-outline:hover {
  border-color: #10b981;
  color: #10b981;
}

.btn-load-more {
  justify-self: center;
  min-width: 180px;
}

.empty-state {
  text-align: center;
  color: #9ca3af;
  font-size: 1.5rem;
  border: 1px dashed #d1d5db;
  border-radius: 10px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
}

@media (max-width: 720px) {
  .form-row {
    grid-template-columns: 1fr;
  }

  .announcements-container {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .biz-announcements-page {
    padding: 16px 12px;
  }

  .page-header h1 {
    font-size: 2.2rem;
  }

  .section-card {
    padding: 16px 12px;
    border-radius: 8px;
  }

  .section-card-header h2 {
    font-size: 1.8rem;
  }

  .btn-primary,
  .btn-green,
  .btn-outline {
    width: 100%;
    text-align: center;
  }
}
</style>